import { useState, useEffect } from 'react';
import { addBorrowerPair, updateBorrowerPair } from '../../services/borrowerApi';
import { Eye, EyeOff, X } from 'lucide-react';
import { ssnOnInput, ssnOnBlur } from '../../utils/format';

const emptyPair = {
  borrower: { firstName: '', middleName: '', lastName: '', suffix: '', taxIdentificationIdentifier: '' },
  coborrower: { firstName: '', middleName: '', lastName: '', suffix: '', taxIdentificationIdentifier: '' },
};

const AddEditBorrowerPairSection = ({ isVisible, editMode, pairData, token, loanid, onCancel, onSuccess, showWarningToast, setWarningMessage, setLoading }) => {
  const [formData, setFormData] = useState(emptyPair);
  const [showSSN, setShowSSN] = useState(false);

  useEffect(() => {
    if (editMode && pairData) {
      setFormData({
        borrower: { ...emptyPair.borrower, ...pairData.borrower },
        coborrower: { ...emptyPair.coborrower, ...pairData.coborrower },
      });
    } else {
      setFormData(emptyPair);
    }
  }, [editMode, pairData]);

  const handleChange = (role, field, value) => {
    setFormData(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    const { firstName, lastName } = formData.borrower;
    if (!firstName.trim() || !lastName.trim()) {
      setWarningMessage("Borrower First Name and Last Name are required.");
      showWarningToast(true);
      return;
    }

    const payload = {
      borrower: {
        borrowerType: 'Individual',
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        middleName: formData.borrower.middleName.trim(),
        suffixToName: formData.borrower.suffix.trim(),
        taxIdentificationIdentifier: formData.borrower.taxIdentificationIdentifier.trim(),
        isBorrower: true
      },
      coborrower: formData.coborrower.firstName.trim() ? {
        borrowerType: 'Individual',
        firstName: formData.coborrower.firstName.trim(),
        lastName: formData.coborrower.lastName.trim(),
        middleName: formData.coborrower.middleName.trim(),
        suffixToName: formData.coborrower.suffix.trim(),
        taxIdentificationIdentifier: formData.coborrower.taxIdentificationIdentifier.trim(),
        isBorrower: true
      } : undefined
    };

    try {
      setLoading?.(true);
      if (editMode) {
        await updateBorrowerPair(token, loanid, pairData.legacyId, payload);
      } else {
        await addBorrowerPair(token, loanid, payload);
      }
      onSuccess(pairData?.legacyId);
    } catch (err) {
      console.error("Submit failed", err);
    } finally {
      setLoading?.(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
      <div className="bg-white rounded shadow-lg w-[900px] p-6">
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h2 className="text-lg font-semibold text-gray-800">{editMode ? 'Edit Borrower Pair' : 'Add Borrower Pair'}</h2>
          <button className="text-gray-500 hover:text-gray-800" onClick={onCancel}><X /></button>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {['borrower', 'coborrower'].map((role, index) => (
            <div key={role}>
              <h2 className="text-base text-gray-600 mb-3">{index === 0 ? 'Borrower' : 'Co-Borrower'}</h2>
              {['firstName', 'middleName', 'lastName', 'suffix'].map(field => (
                <div className="mb-3" key={field}>
                  <label className="form-label text-sm text-[var(--slate-grey-2)]">
                    {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData[role][field] || ''}
                    onChange={e => handleChange(role, field, e.target.value)}
                  />
                </div>
              ))}
              <div className="mb-3 relative">
                <label className="form-label text-sm text-[var(--slate-grey-2)]">SSN / ITIN</label>
                <input
                  type={showSSN ? 'text' : 'password'}
                  className="form-input pr-10"
                  value={formData[role].taxIdentificationIdentifier || ''}
                  onInput={ssnOnInput}
                  onBlur={ssnOnBlur}
                  onChange={e => handleChange(role, 'taxIdentificationIdentifier', e.target.value)}
                />
                <span
                  className="absolute right-3 top-[30px] cursor-pointer text-gray-500"
                  onClick={() => setShowSSN(!showSSN)}
                >
                  {showSSN ? <EyeOff size={18} /> : <Eye size={18} />}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            className="btn-custom border border-gray-300 rounded text-[#2080C3] bg-[#FFFFFF] active:bg-white transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-custom rounded text-[#FFFFFF] bg-[#2080C3] hover:text-[#E6EEF8] hover:bg-[#5DA5D9] active:bg-white transition-colors duration-200"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditBorrowerPairSection;
